import { useCallback, useEffect, useRef, useState, type FocusEvent, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { Check, Mail, Phone, UserCheck } from 'lucide-react';
import type { CrmContact, CrmDealDetail, CrmStage, CrmUserOption, PatchCrmDealDto } from '@ip/shared-types';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useCrmApi } from '@/services/api';
import { cn } from '@/lib/utils';
import { t } from '@/i18n';
import { initialsFromName } from '@/lib/hr';
import { formatMoney, parseDecimal } from '@/utils/currency';
import { InlineField } from './InlineField';
import { EDITOR_LAYER_SELECTOR, editorKeyHandlers, type FocusRequest, type InlineEditorApi } from './inlineEditing';
import { contactName } from './contact';
import { fmtDate, isClosedDeal, isPastDue, weightedValue } from './dealMath';

const CURRENCIES = ['PLN', 'EUR', 'USD', 'GBP'] as const;

/** Last day of the current calendar quarter (no dayjs quarterOfYear plugin loaded). */
function endOfQuarter(): dayjs.Dayjs {
  const today = dayjs();
  const lastMonthOfQuarter = Math.floor(today.month() / 3) * 3 + 2;
  return today.month(lastMonthOfQuarter).endOf('month');
}
const NONE = '__none__';
const NOTES_DEBOUNCE_MS = 800;
const SAVED_FLASH_MS = 1500;

interface Props {
  nip: string;
  deal: CrmDealDetail;
  stage?: CrmStage;
  contacts: CrmContact[];
  /** Owner picker options — fetched the first time the row is edited (cached by the sheet). */
  loadUsers: () => Promise<CrmUserOption[]>;
  currentUserId: string | null;
  now: Date;
  /** Detail + lists still loading — rows render skeletons instead of values. */
  loading: boolean;
  onSaved: (detail: CrmDealDetail) => void;
  /** The `tel:` button was clicked — the composer pre-selects "Rozmowa". */
  onCallClick?: () => void;
  /** Opens the value / expected-close editor from the next-step card. */
  focusRequest?: FocusRequest | null;
}

function Row({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={cn('grid grid-cols-[7.5rem_1fr] items-start gap-2 py-1.5 text-sm', className)}>
      <span className="text-muted-foreground pt-1">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** Focus left the editor for good — Radix Select/Popover portals count as "still inside". */
function leavesEditor(event: FocusEvent<HTMLElement>): boolean {
  const next = event.relatedTarget as HTMLElement | null;
  if (!next) return true;
  if (event.currentTarget.contains(next)) return false;
  return !next.closest(EDITOR_LAYER_SELECTOR);
}

/**
 * An editor that opens as a popover anchored on the row's own display (date
 * picker, owner picker): the row keeps looking like itself while the floating
 * layer is up, and dismissing the layer without a pick reverts the row.
 */
function AnchoredPopoverEditor({
  api,
  className,
  children,
}: {
  api: Pick<InlineEditorApi<never>, 'display' | 'saving' | 'cancel'>;
  className: string;
  children: ReactNode;
}) {
  return (
    <Popover open onOpenChange={(open) => !open && !api.saving && api.cancel()}>
      <PopoverTrigger asChild>
        <button type="button" className="min-h-7 w-full text-left">
          {api.display}
        </button>
      </PopoverTrigger>
      <PopoverContent className={className} align="start">
        {children}
      </PopoverContent>
    </Popover>
  );
}

/** Value + currency editor: Enter or leaving the row saves both keys together. */
function ValueEditor({
  api,
  valueNet,
  currency,
}: {
  api: InlineEditorApi<{ valueNet: number | null; currency: string }>;
  valueNet: number | null;
  currency: string;
}) {
  const [text, setText] = useState(valueNet === null ? '' : String(valueNet).replace('.', ','));
  const [cur, setCur] = useState(currency);

  const commit = () => {
    const parsed = parseDecimal(text);
    if (text.trim() !== '' && parsed === null) {
      api.cancel();
      return;
    }
    api.commit({ valueNet: parsed, currency: cur });
  };

  return (
    <div
      className="flex w-full items-center gap-1.5"
      onBlur={(event) => {
        if (leavesEditor(event) && !api.saving) commit();
      }}
    >
      <Input
        autoFocus
        inputMode="decimal"
        value={text}
        disabled={api.saving}
        onChange={(event) => setText(event.target.value)}
        {...editorKeyHandlers(commit, api.cancel)}
        className="h-8 text-sm tabular-nums"
      />
      <Select value={cur} onValueChange={setCur} disabled={api.saving}>
        <SelectTrigger size="sm" className="w-22 shrink-0">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CURRENCIES.map((code) => (
            <SelectItem key={code} value={code}>
              {code}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function NumberEditor({
  api,
  initial,
  min,
  max,
}: {
  api: InlineEditorApi<number | null>;
  initial: number | null;
  min: number;
  max: number;
}) {
  const [text, setText] = useState(initial === null ? '' : String(initial));
  const commit = () => {
    if (text.trim() === '') {
      api.commit(null);
      return;
    }
    const value = Number(text);
    if (!Number.isFinite(value) || value < min || value > max) {
      api.cancel();
      return;
    }
    api.commit(Math.round(value));
  };
  return (
    <Input
      autoFocus
      type="number"
      min={min}
      max={max}
      value={text}
      disabled={api.saving}
      onChange={(event) => setText(event.target.value)}
      onBlur={() => !api.saving && commit()}
      {...editorKeyHandlers(commit, api.cancel)}
      className="h-8 w-24 text-sm tabular-nums"
    />
  );
}

function DateEditor({ api, initial }: { api: InlineEditorApi<string | null>; initial: string | null }) {
  const selected = initial ? dayjs(initial).toDate() : undefined;
  const pick = (date: Date | undefined) => api.commit(date ? dayjs(date).format('YYYY-MM-DD') : null);
  const quick: Array<{ key: 'week' | 'month' | 'quarter' | 'clear'; date: Date | null }> = [
    { key: 'week', date: dayjs().add(1, 'week').toDate() },
    { key: 'month', date: dayjs().add(1, 'month').toDate() },
    { key: 'quarter', date: endOfQuarter().toDate() },
    { key: 'clear', date: null },
  ];
  return (
    <AnchoredPopoverEditor api={api} className="w-auto p-2">
      <div className="flex flex-wrap gap-1 px-1 pb-1">
        {quick.map(({ key, date }) => (
          <Button
            key={key}
            variant={key === 'clear' ? 'ghost' : 'secondary'}
            size="xs"
            disabled={api.saving || (key === 'clear' && initial === null)}
            onClick={() => pick(date ?? undefined)}
          >
            {t(`crm.deals.facts.quick.${key}`)}
          </Button>
        ))}
      </div>
      {/* Re-clicking the selected day yields undefined — clearing is the chip's job, not a mis-click's. */}
      <Calendar mode="single" selected={selected} defaultMonth={selected} onSelect={(date) => date && pick(date)} />
    </AnchoredPopoverEditor>
  );
}

function OwnerEditor({
  api,
  users,
  onOpen,
  currentUserId,
  currentOwnerId,
}: {
  api: InlineEditorApi<string | null>;
  /** null while the list is still loading. */
  users: CrmUserOption[] | null;
  onOpen: () => void;
  currentUserId: string | null;
  currentOwnerId: string | null;
}) {
  useEffect(() => onOpen(), [onOpen]);
  const me = currentUserId ? users?.find((user) => user.id === currentUserId) : undefined;
  return (
    <AnchoredPopoverEditor api={api} className="w-72 p-0">
      <Command>
        <CommandInput placeholder={t('crm.deals.facts.searchUser')} />
        <CommandList>
          {users === null ? (
            <div className="space-y-2 p-2">
              {Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} className="h-6 w-full" />
              ))}
            </div>
          ) : (
            <CommandEmpty>{t('errors.notFound')}</CommandEmpty>
          )}
          {me && me.id !== currentOwnerId ? (
            <>
              <CommandGroup>
                {/* cmdk filters on the value: the "assign me" row must match the user's own name too. */}
                <CommandItem value={`me ${me.displayName}`} onSelect={() => api.commit(me.id)}>
                  <UserCheck className="size-4" />
                  {t('crm.deals.facts.assignMe')}
                </CommandItem>
              </CommandGroup>
              <CommandSeparator />
            </>
          ) : null}
          <CommandGroup>
            {(users ?? []).map((user) => (
              <CommandItem
                key={user.id}
                value={`${user.displayName} ${user.email}`}
                onSelect={() => api.commit(user.id)}
              >
                <Avatar className="size-5">
                  <AvatarFallback className="text-[9px]">{initialsFromName(user.displayName)}</AvatarFallback>
                </Avatar>
                <span className="truncate">{user.displayName}</span>
                {user.id === currentOwnerId ? <Check className="ml-auto size-4" /> : null}
              </CommandItem>
            ))}
          </CommandGroup>
          <CommandSeparator />
          <CommandGroup>
            <CommandItem
              value={`none ${t('crm.deals.facts.noOwner')}`}
              disabled={currentOwnerId === null}
              onSelect={() => api.commit(null)}
              className="text-muted-foreground"
            >
              {t('crm.deals.facts.noOwner')}
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </AnchoredPopoverEditor>
  );
}

function ContactEditor({
  api,
  contacts,
  current,
}: {
  api: InlineEditorApi<string | null>;
  contacts: CrmContact[];
  current: string | null;
}) {
  // The select opens immediately; closing it without a pick reverts the row.
  const picked = useRef(false);
  return (
    <Select
      defaultOpen
      value={current ?? NONE}
      disabled={api.saving}
      onValueChange={(value) => {
        picked.current = true;
        api.commit(value === NONE ? null : value);
      }}
      onOpenChange={(open) => {
        if (!open && !picked.current && !api.saving) api.cancel();
      }}
    >
      <SelectTrigger size="sm" className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>{t('crm.deals.facts.noContact')}</SelectItem>
        {contacts.map((contact) => (
          <SelectItem key={contact.id} value={contact.id}>
            {contactName(contact)}
            {contact.isPrimary ? ' ★' : ''}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Notes save themselves: 800 ms after the last keystroke or on blur, one PUT
 * with just `{notes}`. A "Zapisano" flash is the only feedback — no button.
 */
function NotesField({
  value,
  onSave,
}: {
  value: string | null;
  onSave: (notes: string | null) => Promise<void>;
}) {
  const [text, setText] = useState(value ?? '');
  const [saved, setSaved] = useState(false);
  const lastSaved = useRef(value ?? '');
  const timer = useRef<number | null>(null);
  const savedTimer = useRef<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // A fresh detail (e.g. after a stage change) only replaces the text when
  // the user is not mid-edit; otherwise their unsaved keystrokes would vanish.
  useEffect(() => {
    if ((value ?? '') !== lastSaved.current && text === lastSaved.current) {
      lastSaved.current = value ?? '';
      setText(value ?? '');
    }
  }, [value, text]);

  const flush = useCallback(async () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
    const next = text;
    if (next === lastSaved.current) return;
    try {
      await onSave(next.trim() === '' ? null : next);
      lastSaved.current = next;
      setSaved(true);
      if (savedTimer.current) window.clearTimeout(savedTimer.current);
      savedTimer.current = window.setTimeout(() => setSaved(false), SAVED_FLASH_MS);
    } catch {
      toast.error(t('errors.saveFailed'));
    }
  }, [text, onSave]);

  useEffect(() => {
    if (text === lastSaved.current) return;
    timer.current = window.setTimeout(() => void flush(), NOTES_DEBOUNCE_MS);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [text, flush]);

  useEffect(
    () => () => {
      if (savedTimer.current) window.clearTimeout(savedTimer.current);
    },
    [],
  );

  // Auto-grow: the textarea follows its content instead of scrolling inside.
  useEffect(() => {
    const element = textareaRef.current;
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  }, [text]);

  return (
    <div className="space-y-1">
      <Textarea
        ref={textareaRef}
        rows={3}
        value={text}
        placeholder={t('crm.deals.facts.notesPlaceholder')}
        onChange={(event) => setText(event.target.value)}
        onBlur={() => void flush()}
        onKeyDown={(event: KeyboardEvent<HTMLTextAreaElement>) => {
          // Esc inside the notes must not close the sheet — it just drops focus.
          if (event.key === 'Escape') {
            event.stopPropagation();
            event.currentTarget.blur();
          }
        }}
        className="min-h-20 resize-none overflow-hidden text-sm"
      />
      <p
        aria-live="polite"
        className={cn(
          'h-3 text-[11px] text-emerald-600 transition-opacity motion-reduce:transition-none dark:text-emerald-400',
          saved ? 'opacity-100' : 'opacity-0',
        )}
      >
        {t('crm.deals.facts.savedFlash')}
      </p>
    </div>
  );
}

/**
 * "Dane" — the deal's facts as click-to-edit rows. Every save is a single-key
 * `PUT` (PatchCrmDealDto), so two people editing different rows never
 * clobber each other's fields.
 */
export function DealFacts({
  nip,
  deal,
  stage,
  contacts,
  loadUsers,
  currentUserId,
  now,
  loading,
  onSaved,
  onCallClick,
  focusRequest,
}: Props) {
  const { updateDeal } = useCrmApi();
  const closed = isClosedDeal(deal, stage);

  const [users, setUsers] = useState<CrmUserOption[] | null>(null);
  const ensureUsers = useCallback(() => {
    loadUsers()
      .then(setUsers)
      .catch(() => {
        // An empty picker plus the toast beats a spinner nobody can dismiss.
        setUsers([]);
        toast.error(t('errors.loadingFailed'));
      });
  }, [loadUsers]);

  const patch = useCallback(
    async (dto: PatchCrmDealDto) => {
      const detail = await updateDeal(nip, deal.id, dto);
      onSaved(detail);
    },
    [updateDeal, nip, deal.id, onSaved],
  );

  const weighted = weightedValue(deal);
  const pastDue = !closed && isPastDue(deal, now);
  const primaryContact = deal.primaryContactId ? contacts.find((c) => c.id === deal.primaryContactId) : undefined;
  const stageDefault = stage?.probability ?? null;

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 8 }).map((_, index) => (
          <div key={index} className="grid grid-cols-[7.5rem_1fr] gap-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-full" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="divide-y">
      <Row label={t('crm.deals.facts.value')}>
        <InlineField<{ valueNet: number | null; currency: string }>
          openRequest={focusRequest?.target === 'value' ? focusRequest.nonce : undefined}
          display={
            <span className={cn('tabular-nums', deal.valueNet === null && 'text-muted-foreground')}>
              {deal.valueNet === null ? '—' : formatMoney(deal.valueNet, deal.currency, 2)}
            </span>
          }
          editor={(api) => <ValueEditor api={api} valueNet={deal.valueNet} currency={deal.currency} />}
          onSave={(value) => patch(value)}
        />
      </Row>

      <Row label={t('crm.deals.facts.probability')}>
        <InlineField<number | null>
          display={
            <span className="flex items-center gap-2">
              <span className="tabular-nums">{deal.probability === null ? '—' : `${deal.probability} %`}</span>
              {stageDefault !== null && deal.probability === stageDefault ? (
                <span className="text-muted-foreground text-xs">
                  {t('crm.deals.facts.stageDefault', { p: stageDefault })}
                </span>
              ) : null}
            </span>
          }
          editor={(api) => <NumberEditor api={api} initial={deal.probability} min={0} max={100} />}
          onSave={(probability) => patch({ probability })}
        />
        {/* Plain helper text, not a tooltip: an SVG inside the edit button is neither focusable nor hoverable on touch. */}
        <p className="text-muted-foreground text-[11px]">{t('crm.deals.facts.stageOverrides')}</p>
      </Row>

      <Row label={t('crm.deals.facts.weighted')}>
        <span className={cn('tabular-nums', weighted === null && 'text-muted-foreground')}>
          {weighted === null ? '—' : formatMoney(weighted, deal.currency, 2)}
        </span>
      </Row>

      <Row label={t('crm.deals.facts.expectedClose')}>
        <InlineField<string | null>
          openRequest={focusRequest?.target === 'date' ? focusRequest.nonce : undefined}
          display={
            <span className={cn(deal.expectedCloseAt === null && 'text-muted-foreground')}>
              {deal.expectedCloseAt ? fmtDate(deal.expectedCloseAt) : '—'}
              {pastDue ? <span className="text-destructive ml-1.5">{t('crm.deals.facts.pastDue')}</span> : null}
            </span>
          }
          editor={(api) => <DateEditor api={api} initial={deal.expectedCloseAt} />}
          onSave={(expectedCloseAt) => patch({ expectedCloseAt })}
        />
      </Row>

      <Row label={t('crm.deals.facts.owner')}>
        <InlineField<string | null>
          display={
            deal.ownerDisplayName ? (
              <span className="flex items-center gap-2">
                <Avatar className="size-5">
                  <AvatarFallback className="text-[9px]">{initialsFromName(deal.ownerDisplayName)}</AvatarFallback>
                </Avatar>
                {deal.ownerDisplayName}
              </span>
            ) : (
              <span className="text-muted-foreground italic">{t('crm.deals.card.noOwner')}</span>
            )
          }
          editor={(api) => (
            <OwnerEditor
              api={api}
              users={users}
              onOpen={ensureUsers}
              currentUserId={currentUserId}
              currentOwnerId={deal.ownerUserId}
            />
          )}
          onSave={(ownerUserId) => patch({ ownerUserId })}
        />
      </Row>

      <Row label={t('crm.deals.facts.account')}>
        <Link to={`/${nip}/crm/klienci/${deal.accountId}`} className="text-primary hover:underline">
          {deal.accountName}
        </Link>
      </Row>

      <Row label={t('crm.deals.facts.primaryContact')}>
        <InlineField<string | null>
          display={
            primaryContact ? (
              <span>{contactName(primaryContact)}</span>
            ) : (
              <span className="text-muted-foreground">{t('crm.deals.facts.noContact')}</span>
            )
          }
          editor={(api) => <ContactEditor api={api} contacts={contacts} current={deal.primaryContactId} />}
          onSave={(primaryContactId) => patch({ primaryContactId })}
        />
        {primaryContact && (primaryContact.phone || primaryContact.email) ? (
          <div className="text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
            {primaryContact.phone ? (
              <a
                href={`tel:${primaryContact.phone}`}
                onClick={onCallClick}
                className="hover:text-foreground inline-flex items-center gap-1"
              >
                <Phone className="size-3" />
                {primaryContact.phone}
              </a>
            ) : null}
            {primaryContact.email ? (
              <a
                href={`mailto:${primaryContact.email}`}
                className="hover:text-foreground inline-flex items-center gap-1"
              >
                <Mail className="size-3" />
                {primaryContact.email}
              </a>
            ) : null}
          </div>
        ) : null}
      </Row>

      <Row label={t('crm.deals.facts.project')}>
        <span className={cn(!deal.projectName && 'text-muted-foreground')}>{deal.projectName ?? '—'}</span>
      </Row>

      <Row label={t('crm.deals.facts.notes')} className="items-start">
        <NotesField value={deal.notes} onSave={(notes) => patch({ notes })} />
      </Row>

      <p className="text-muted-foreground pt-3 text-[11px]">
        {t('crm.deals.facts.createdAt')} {fmtDate(deal.createdAt, true)}
        {' · '}
        {t('crm.deals.facts.updatedAt')} {fmtDate(deal.updatedAt, true)}
      </p>
    </div>
  );
}
